import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { useSyncSystem } from './sync-system-context';
import { onLocalReady, onSyncComplete } from './sync-event-bus';
import { useEntityStore } from './entity-store';
import { setAwarenessUser } from './awareness-user';
import { useAuthStore } from '@/stores/use-auth-store';
import type { YjsRoomKind } from './yjs-registry';

/**
 * Pure resolver for an entity's Yjs room kind. Extracted from the inline
 * selector below so it is unit-testable.
 *
 * Yjs-backed kinds (document/canvas/space) are read from the store;
 * non-Yjs-backed kinds (collection, recording) fall through to the explicit
 * hint. The server resolves the authoritative kind regardless — this only
 * shapes the WS subscribe envelope's `entityKind` and pre-bootstrap behavior.
 */
export function resolveYjsRoomKind(
  state: Pick<ReturnType<typeof useEntityStore.getState>, 'entityMeta' | 'canvases' | 'spaces'>,
  entityId: string,
  entityKindHint: YjsRoomKind | undefined,
): YjsRoomKind | null {
  if (state.entityMeta.has(entityId)) return state.entityMeta.get(entityId)!.kind;
  if (state.canvases.has(entityId)) return 'canvas';
  if (state.spaces.has(entityId)) return 'space';
  return entityKindHint ?? null;
}

/**
 * Subscribe to an entity's Yjs room.
 *
 * The second parameter is a HINT for the entity's kind, used as a fallback
 * when `resolveEntityKind` (entityStore lookup) returns null. The server
 * resolves the authoritative kind from `entities.kind` regardless of what we
 * send — this hint exists to give the WS subscribe envelope a sensible
 * `entityKind` field for non-entity-table rooms (e.g., workspace) and as a
 * pre-bootstrap fallback for entity rooms.
 *
 * Cold deep-link safety: if the entity is not yet in the store and no hint
 * is provided, we DEFER subscribing until the store hydrates. This prevents
 * the client from sending `entityKind: 'document'` for a Space's UUID, which
 * the server-side authoritative resolver corrects but is still confusing in
 * logs and creates an extra round-trip.
 */
export function useEntityYjs(entityId: string, entityKindHint?: YjsRoomKind) {
  const { registry } = useSyncSystem();

  // Subscribe to entityStore: this hook is what forces a re-render when the
  // store mutates, so resolveEntityKind below picks up newly-hydrated rows.
  // We compute the resolved kind below (a primitive string|null) and use
  // ONLY that primitive in the effect's dep array — including the Maps
  // themselves would re-run the effect on every entity event for any UUID,
  // tearing down the WS subscription and restarting sync mid-flight.
  const resolvedKind = useEntityStore((s): YjsRoomKind | null =>
    resolveYjsRoomKind(s, entityId, entityKindHint),
  );

  // Eagerly initialize if doc is already subscribed and ready — avoids an
  // extra render cycle through the skeleton when navigating between docs.
  const [doc, setDoc] = useState<Y.Doc | null>(() => {
    const existing = registry.getDoc(entityId);
    const localReady = registry.getEntry(entityId)?.localReady ?? false;
    return existing && localReady ? existing : null;
  });
  const [awareness, setAwareness] = useState<Awareness | null>(() => {
    const localReady = registry.getEntry(entityId)?.localReady ?? false;
    return localReady ? (registry.getAwareness(entityId) ?? null) : null;
  });
  // Eagerly initialize from registry: if another component already subscribed
  // and the room finished syncing before this hook attached its listener, the
  // syncComplete event has already fired. Without this, `synced` would never
  // flip to true and the loading skeleton stays visible forever.
  const [synced, setSynced] = useState<boolean>(
    () => registry.getEntry(entityId)?.serverSynced ?? false,
  );

  useEffect(() => {
    // Defer subscribe until the entity's kind is known. On cold deep-links the
    // resolved kind is null until the store hydrates from bootstrap; subscribing
    // with a guessed kind sends a wrong-kind hint to the server and (until the
    // server resolves authoritatively) leaves the room mis-typed in flight.
    if (!resolvedKind) return;

    // EntitySubscriber (router.tsx) is the primary subscription site and covers
    // cold deep-link boots. This call is belt-and-suspenders for normal tab opens
    // where the editor may mount before EntitySubscriber's effect fires.
    // registry.subscribe is idempotent — safe to call from both places.
    void registry.subscribe(entityId, resolvedKind);

    const setReady = () => {
      const yDoc = registry.getDoc(entityId);
      if (yDoc) setDoc(yDoc);
      const aw = registry.getAwareness(entityId) ?? null;
      // Register the local user on this doc's awareness as soon as it's ready.
      // This is the central, race-free init: the presence bridge only set the
      // user on docs subscribed at the moment it ran, so a doc subscribed later
      // never got a user — and CollaborationCaret's setLocalStateField no-ops on
      // null state. setAwarenessUser uses setLocalState (always-writes).
      const user = useAuthStore.getState().user;
      if (aw && user) setAwarenessUser(aw, user);
      setAwareness(aw);
    };

    // If already ready (e.g. re-subscribing same doc), set immediately
    const entry = registry.getEntry(entityId);
    if (entry?.localReady) {
      setReady();
    }
    // If the room already finished syncing before this listener attached,
    // we'd miss the syncComplete event entirely. Catch the already-synced
    // state here so `synced` flips to true on mount.
    if (entry?.serverSynced) {
      setSynced(true);
    }

    const unsubLocal = onLocalReady((id) => {
      if (id === entityId) setReady();
    });

    const unsubSync = onSyncComplete((id) => {
      if (id === entityId) {
        setSynced(true);
        setReady();
      }
    });

    registry.startViewing(entityId);

    return () => {
      unsubLocal();
      unsubSync();
      registry.stopViewing(entityId);
      // NOTE: Do NOT unsubscribe here. Unsubscribing decrements refCount and
      // (if it hits zero) schedules teardown of the Y.Doc, IndexedDB binding,
      // and Awareness instance — forcing a full re-sync on re-mount. The app
      // currently holds registry entries alive for the full session: there
      // is no call site that invokes registry.unsubscribe(entityId) from closeTab
      // or similar user actions, so entries persist until page reload or
      // deletePermanently(). Keep this path as stopViewing-only so tab
      // switching and tab close+reopen stay fast.
      setDoc(null);
      setAwareness(null);
      setSynced(false);
    };
  }, [registry, entityId, resolvedKind]);

  return { doc, awareness, synced };
}
